import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { getCached, setCached, invalidate } from '../lib/queryCache'
import { C, R, F, btnPrimary, btnSecondary, btnDanger, btnGhost, card, input, thStyle, pageHeader, sectionLabel, kpiCard, statusBadge, spinner } from '../lib/designTokens'

// ─── Icons ────────────────────────────────────────────────────────────────────
const Icon = ({ d, size=14 }) => <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d={d}/></svg>
const PlusIcon   = () => <Icon d="M8 2v12M2 8h12"/>
const SearchIcon = () => <Icon d="M7 13A6 6 0 107 1a6 6 0 000 12zM14 14l-3-3"/>
const FolderIcon = () => <Icon d="M2 5h4l2-2h6v9a1 1 0 01-1 1H3a1 1 0 01-1-1V5z" size={20}/>

const fmt = n => Number(n||0).toLocaleString('en-US',{maximumFractionDigits:0})
const fmtVal = (v, cur) => {
  const n = parseFloat(v)||0
  const s = n>=1e9?(n/1e9).toFixed(1)+'B':n>=1e6?(n/1e6).toFixed(1)+'M':fmt(n)
  return `${s} ${cur||'SAR'}`
}

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => (
  <div style={{ position:'fixed', bottom:24, right:24, zIndex:999, display:'flex', alignItems:'center', gap:10, padding:'11px 18px', borderRadius:R.card, boxShadow:'0 4px 20px rgba(0,0,0,.18)', fontSize:F.body, fontWeight:600, background: type==='success'?C.green:C.red, color:'#fff', minWidth:240 }}>
    <span style={{flex:1}}>{message}</span>
    <button onClick={onClose} style={{background:'none',border:'none',color:'rgba(255,255,255,.7)',cursor:'pointer',fontSize:16,lineHeight:1}}>×</button>
  </div>
)

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = statusBadge(status)
  return (
    <span style={{ fontSize:F.caption, fontWeight:600, padding:'2px 9px', borderRadius:R.badge, background:s.bg, color:s.color, border:`1px solid ${s.border}`, whiteSpace:'nowrap' }}>
      {status?.replace('_',' ')}
    </span>
  )
}

// ─── Project Modal ────────────────────────────────────────────────────────────
const ProjectModal = ({ project, orgs, currencies, onSave, onClose }) => {
  const [form, setForm] = useState(project || {
    organization_id: orgs[0]?.id || '', name:'', name_ar:'',
    project_no:'', project_address:'',
    client_name:'', main_contractor:'',
    contract_value:'', currency:'SAR', start_date:'', end_date:'',
    status:'active', enable_boq_lines:true,
  })
  const h = (k,v) => setForm(f=>({...f,[k]:v}))

  return (
    <div style={{position:'fixed',inset:0,zIndex:200,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{position:'absolute',inset:0,background:'rgba(0,0,0,.45)',backdropFilter:'blur(2px)'}} onClick={onClose}/>
      <div style={{position:'relative',background:'#fff',borderRadius:R.card+4,boxShadow:'0 20px 60px rgba(0,0,0,.2)',width:'100%',maxWidth:480,margin:'0 16px',padding:28,maxHeight:'90vh',overflowY:'auto'}}>
        <h3 style={{fontSize:F.heading-2,fontWeight:700,color:C.ink,marginBottom:20}}>{project?'Edit Project':'New Project'}</h3>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {[
            ['Organization *', 'organization_id', 'select'],
            ['Project Name (EN) *', 'name', 'text', 'e.g. Jeddah Tower Construction'],
            ['اسم المشروع (AR)', 'name_ar', 'text', 'مثال: مشروع برج جدة'],
            ['Project No.', 'project_no', 'text', 'e.g. 1020096'],
            ['Project Address', 'project_address', 'text', 'e.g. JCD - Oceanarium Site'],
            ['Client', 'client_name', 'text', 'e.g. Saudi Entertainment Ventures'],
            ['Main Contractor', 'main_contractor', 'text', 'e.g. Al Bawani Co.'],
            ['Contract Value', 'contract_value', 'number'],
            ['Currency', 'currency', 'select-currency'],
            ['Start Date', 'start_date', 'date'],
            ['End Date', 'end_date', 'date'],
            ['Status', 'status', 'select-status'],
          ].map(([label, key, type, placeholder]) => (
            <div key={key}>
              <label style={{display:'block',fontSize:F.caption,fontWeight:600,color:C.sub,marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>{label}</label>
              {type==='select' ? (
                <select value={form[key]} onChange={e=>h(key,e.target.value)} style={{...input}}>
                  {orgs.map(o=><option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              ) : type==='select-currency' ? (
                <select value={form[key]} onChange={e=>h(key,e.target.value)} style={{...input}}>
                  {currencies.map(c=><option key={c.code} value={c.code}>{c.code}</option>)}
                </select>
              ) : type==='select-status' ? (
                <select value={form[key]} onChange={e=>h(key,e.target.value)} style={{...input}}>
                  {['active','completed','on_hold','cancelled'].map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
                </select>
              ) : (
                <input type={type} value={form[key]||''} onChange={e=>h(key,e.target.value)} placeholder={placeholder||''}
                  dir={key==='name_ar'?'rtl':'ltr'} style={{...input}}/>
              )}
            </div>
          ))}
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <input type="checkbox" id="boq" checked={form.enable_boq_lines} onChange={e=>h('enable_boq_lines',e.target.checked)} style={{width:15,height:15,accentColor:C.blue}}/>
            <label htmlFor="boq" style={{fontSize:F.body,color:C.ink2,cursor:'pointer'}}>Enable BOQ Lines</label>
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:24}}>
          <button onClick={onClose} style={{...btnSecondary,flex:1,justifyContent:'center'}}>Cancel</button>
          <button onClick={()=>onSave(form)} style={{...btnPrimary,flex:1,justifyContent:'center'}}>{project?'Save Changes':'Create Project'}</button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Projects({ onSelectProject, selectedProject }) {
  // Projects is core platform administration (org_id/status changes
  // affect has_permission() for every user in the project) -- gated on
  // platform-admin status only, not useProjectAuth (no single project
  // context applies here; this page spans all projects/orgs).
  const { can } = useAuth()
  const isAdmin = !!can?.is_admin
  const [projects,     setProjects]     = useState([])
  const [orgs,         setOrgs]         = useState([])
  const [currencies,   setCurrencies]   = useState([])
  const [loading,      setLoading]      = useState(true)
  const [modal,        setModal]        = useState(null)
  const [toast,        setToast]        = useState(null)
  const [search,       setSearch]       = useState('')
  const [orgFilter,    setOrgFilter]    = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')

  const showToast = (message, type='success') => { setToast({message,type}); setTimeout(()=>setToast(null),4000) }

  const loadData = useCallback(async () => {
    setLoading(true)
    const [{ data: pData }, { data: oData }, { data: mData }, { data: curData }] = await Promise.all([
      supabase.from('projects').select('id,name,name_ar,project_no,project_address,client_name,main_contractor,contract_value,currency,start_date,end_date,status,enable_boq_lines,organization_id,organizations(name)').order('name'),
      supabase.from('organizations').select('id,name').order('name'),
      // Read-only count of linked Estimation Tenders per Project, via the
      // approved tender_costspace_project_mapping table (Phase A/B).
      // This client defaults to a different schema than the mapping
      // table lives in, so the schema must be set explicitly here --
      // same pattern already proven working in Phase B's Tender
      // Workspace fix. No write, no new RPC, no schema/RLS change.
      supabase.schema('estimation').from('tender_costspace_project_mapping').select('costspace_project_id'),
      // Currency master, replacing the previous hardcoded dropdown list --
      // only active currencies are offered for a Project's own currency.
      supabase.from('currencies').select('code,name').eq('is_active', true).order('code'),
    ])
    const tenderCountByProject = {}
    ;(mData || []).forEach((m) => {
      tenderCountByProject[m.costspace_project_id] = (tenderCountByProject[m.costspace_project_id] || 0) + 1
    })
    setProjects((pData || []).map((p) => ({ ...p, linkedTenderCount: tenderCountByProject[p.id] || 0 })))
    setOrgs(oData||[])
    setCurrencies(curData||[])
    setLoading(false)
  }, [])

  useEffect(()=>{ loadData() },[loadData])

  const handleSave = async (form) => {
    if (!form.organization_id||!form.name) return showToast('Organization and name required','error')
    const payload = { organization_id:form.organization_id, name:form.name, name_ar:form.name_ar||null, project_no:form.project_no||null, project_address:form.project_address||null, client_name:form.client_name||null, main_contractor:form.main_contractor||null, contract_value:form.contract_value||0, currency:form.currency||'SAR', start_date:form.start_date||null, end_date:form.end_date||null, status:form.status, enable_boq_lines:form.enable_boq_lines }
    if (modal?.id) {
      const {error} = await supabase.from('projects').update(payload).eq('id',modal.id)
      if (error) { showToast('Update failed: '+error.message,'error') } else { showToast('Project updated'); setModal(null); loadData() }
    } else {
      const {error} = await supabase.from('projects').insert([payload])
      if (error) { showToast('Create failed: '+error.message,'error') } else { showToast('Project created'); setModal(null); loadData() }
    }
  }

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return
    const {error} = await supabase.from('projects').delete().eq('id',p.id)
    if (error) { showToast('Delete failed','error') } else { showToast('Deleted'); loadData() }
  }

  const filtered = projects.filter(p => {
    const q = search.toLowerCase()
    return (!q || p.name.toLowerCase().includes(q) || p.organizations?.name?.toLowerCase().includes(q))
      && (orgFilter==='all' || p.organization_id===orgFilter)
      && (statusFilter==='all' || p.status===statusFilter)
  })

  const stats = {
    total: projects.length,
    active: projects.filter(p=>p.status==='active').length,
    totalValue: projects.reduce((s,p)=>s+(parseFloat(p.contract_value)||0),0),
  }

  return (
    <div style={{minHeight:'100vh',background:C.pageBg,fontFamily:'inherit'}}>

      {/* Header */}
      <div style={{...pageHeader,paddingLeft:24,paddingRight:24}}>
        <div>
          <div style={{fontSize:F.heading,fontWeight:800,color:C.ink,letterSpacing:'-.02em'}}>Projects</div>
          <div style={{fontSize:F.caption,color:C.sub,marginTop:2}}>Manage your construction projects</div>
        </div>
        <div style={{flex:1}}/>
        {isAdmin && (
          <button onClick={()=>orgs.length?setModal('add'):showToast('Create an organization first','error')}
            style={{...btnPrimary,gap:6}}>
            <svg width={14} height={14} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 2v12M2 8h12"/></svg>
            New Project
          </button>
        )}
      </div>

      <div style={{padding:'20px 24px',maxWidth:1200,margin:'0 auto',display:'flex',flexDirection:'column',gap:16}}>

        {/* KPI Cards */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
          {[
            { label:'Total Projects', value:stats.total, fmt:v=>v, color:C.ink },
            { label:'Active',         value:stats.active, fmt:v=>v, color:C.green },
            { label:'Total Contract Value', value:stats.totalValue, fmt:v=>fmtVal(v,'SAR'), color:C.blue },
          ].map(k=>(
            <div key={k.label} style={{...kpiCard}}>
              <div style={{fontSize:F.label,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:C.muted,marginBottom:6}}>{k.label}</div>
              <div style={{fontSize:F.kpi,fontWeight:800,color:k.color,lineHeight:1.1,fontVariantNumeric:'tabular-nums'}}>{k.fmt(k.value)}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
          <div style={{position:'relative',flex:1,minWidth:180}}>
            <svg width={14} height={14} viewBox="0 0 16 16" fill="none" stroke={C.muted} strokeWidth="1.5" style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}><circle cx="7" cy="7" r="5"/><path d="M14 14l-3-3"/></svg>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search projects..."
              style={{...input,paddingLeft:30,width:'100%',boxSizing:'border-box'}}/>
          </div>
          <select value={orgFilter} onChange={e=>setOrgFilter(e.target.value)}
            style={{...input,width:'auto',minWidth:160}}>
            <option value="all">All Organizations</option>
            {orgs.map(o=><option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}
            style={{...input,width:'auto',minWidth:130}}>
            <option value="all">All Status</option>
            {['active','completed','on_hold','cancelled'].map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
          </select>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'60px 0',color:C.sub}}>
            <div style={{...spinner}}/>
            <span style={{fontSize:F.body}}>Loading...</span>
          </div>
        ) : filtered.length===0 ? (
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'64px 0',gap:12,color:C.muted}}>
            <div style={{width:56,height:56,borderRadius:16,background:C.pageBg,border:`2px dashed ${C.border2}`,display:'flex',alignItems:'center',justifyContent:'center'}}>
              <FolderIcon/>
            </div>
            <div style={{fontSize:F.bodyLg,fontWeight:600,color:C.sub}}>No projects found</div>
            <div style={{fontSize:F.body,color:C.muted}}>{orgs.length===0?'Create an organization first':'Try adjusting your filters'}</div>
            {orgs.length>0 && isAdmin && <button onClick={()=>setModal('add')} style={{...btnPrimary,marginTop:4}}>+ New Project</button>}
          </div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:14}}>
            {filtered.map(p=>(
              <div key={p.id} style={{...card,transition:'box-shadow .15s'}}
                onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,.08)'}
                onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
                {/* Card header */}
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:14}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:F.title,fontWeight:700,color:C.ink,marginBottom:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}{p.project_no && <span style={{fontWeight:500,color:C.muted,marginLeft:6}}>#{p.project_no}</span>}</div>
                    {p.name_ar && <div style={{fontSize:F.caption,color:C.sub,direction:'rtl'}}>{p.name_ar}</div>}
                    <div style={{fontSize:F.caption,color:C.muted,marginTop:2}}>{p.organizations?.name}</div>
                  </div>
                  <StatusBadge status={p.status}/>
                </div>

                {/* Card body */}
                <div style={{borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`,padding:'10px 0',marginBottom:14,display:'flex',flexDirection:'column',gap:6}}>
                  {[
                    ['Contract Value', fmtVal(p.contract_value, p.currency)],
                    p.client_name && ['Client', p.client_name],
                    p.main_contractor && ['Main Contractor', p.main_contractor],
                    p.start_date && ['Duration', `${p.start_date} → ${p.end_date||'TBD'}`],
                    ['BOQ Lines', p.enable_boq_lines?'Enabled':'Disabled'],
                    ['Linked Tenders', p.linkedTenderCount ?? 0],
                  ].filter(Boolean).map(([label,val])=>(
                    <div key={label} style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span style={{fontSize:F.caption,color:C.sub}}>{label}</span>
                      <span style={{fontSize:F.caption,fontWeight:600,color:C.ink2}}>{val}</span>
                    </div>
                  ))}
                </div>

                {/* Card footer */}
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
                  {onSelectProject && (
                    <button onClick={()=>onSelectProject(p)}
                      style={{...selectedProject?.id===p.id?{...btnPrimary,background:C.green,fontSize:F.caption,padding:'5px 12px'}:{...btnSecondary,fontSize:F.caption,padding:'5px 12px'}}}>
                      {selectedProject?.id===p.id?'✓ Active':'Select'}
                    </button>
                  )}
                  <div style={{display:'flex',gap:12,marginLeft:'auto'}}>
                    {isAdmin && (
                      <button onClick={()=>setModal(p)} style={{...btnGhost,color:C.blue,fontSize:F.caption,fontWeight:600}}>Edit</button>
                    )}
                    {isAdmin && (
                      <button onClick={()=>handleDelete(p)} style={{...btnGhost,color:C.red,fontSize:F.caption,fontWeight:600}}>Delete</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(modal==='add'||(modal&&modal.id)) && <ProjectModal project={modal==='add'?null:modal} orgs={orgs} currencies={currencies} onSave={handleSave} onClose={()=>setModal(null)}/>}
      {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
